import { UseMutationResult } from '@tanstack/react-query';
import { BotHolidayAPISchema } from './botHoliday';
import { MetaSchema } from './meta';

export enum BotEmailProvider {
  SENDGRID = 'send_grid',
  GMAIL = 'gmail',
  OUTLOOK = 'outlook',
}

export type BotSchema = BotApiSchema['attributes'] & {
  id: string | number;
  botErrors?: BotErrorSchema[];
};

export type BotSchemaWithWarmup = BotSchema & {
  botWarmUp: (BotWarmUpAPISchema['attributes'] & { id: string }) | null;
};

export interface BotEmailMetricsApiSchema {
  bouncedRate: number;
  openRate: number;
  responseRate: number;
  sentCount: number;
}

export interface BotEmailMetricsSchema {
  content: string | null;
  name: string;
  type: 'count' | 'rate' | 'text';
  value: number | string | null;
}

export interface BotUnsubscribePayload {
  unsubscribeText: string;
  useUnsubscribe: boolean;
}

// TODO: remove old states once we're done
export type BotWarmUpState = 'dormant' | 'active' | 'healthy' | 'new' | 'caution' | 'critical';

export interface BotWarmUpAPISchema {
  attributes: {
    activeRate: number;
    botId: string | number;
    createdAt: string;
    currentRate: number;
    dormantRate: number;
    healthyRate: number;
    state: BotWarmUpState;
    updatedAt: string;
    daysRequiredToReachTargetLimit?: number; // TODO: remove optional once API is updated
    previousDayRate?: number; // TODO: remove optional once API is updated
    recommendedSendLimit?: {
      // TODO: remove optional once API is updated
      maxLimit: number;
      minLimit: number;
    };
    // TODO: remove optional once API is updated
    warmUpTargetLimit?: number;
    // TODO: remove optional once API is updated
    warmUpType?: 'auto' | 'manual';
  };
  id: string;
  type: string;
}

export interface BotErrorAPISchema {
  attributes: {
    botId: number;
    category: string;
    createdAt: string;
    details: string;
    updatedAt: string;
  };
  id: string;
  type: 'bot_errors';
}
export type BotErrorSchema = BotErrorAPISchema['attributes'] & { id: string };

export interface BotWarmupRatesPayload {
  activeRate: number;
  dormantRate: number;
  healthyRate: number;
}

export interface UpdateBotWarmupPayload {
  currentRate: BotWarmUpAPISchema['attributes']['currentRate'];
  warmUpType: BotWarmUpAPISchema['attributes']['warmUpType'];
}

export type BotIncludedSchema = (BotWarmUpAPISchema | BotHolidayAPISchema | BotErrorAPISchema)[];

export interface BotApiSchema {
  attributes: {
    createdAt: string;
    email: string;
    emailDomain: string;
    emailProvider: string;
    name: string;
    outreachEmailsSentToday: number;
    paused: boolean;
    picture: string | null;
    restricted: boolean;
    signature: string;
    state: 'connected' | 'disconnected';
    // remove null once BE makes time zone mandatory
    timeZone: string | null;
    unsubscribeText: string;
    unsubscribeUrl: string;
    useUnsubscribe: boolean;
    // make mandatory once all bot fields in other endpoints are updated with emailMetrics and lastEmailSent
    emailMetrics?: BotEmailMetricsApiSchema;
    lastEmailSentAt?: string | null;
    recentAvgBounceRate?: number;
    recentAvgSentRate?: number;
  };
  id: string | number;
  relationships: {
    botWarmUp?: {
      data: {
        id: string;
        type: 'bot_warm_up';
      };
    } | null;
    latestUnresolvedBotErrors?: {
      data: { id: string; type: 'bot_errors' }[];
    };
  };
  type: 'bots';
}

// exclude querying recent_avg_bounce_rate, recent_avg_sent_rate and last_email_sent_at by default
export const DEFAULT_SPARSE_FIELDS = [
  'created_at',
  'deleted_at',
  'disconnected_since',
  'email',
  'email_domain',
  'email_metrics',
  'email_provider',
  'immediate_reach_out_upon_enrolment',
  'name',
  'next_reminder',
  'paused',
  'picture',
  'restricted',
  'signature',
  'state',
  'team_id',
  'time_zone',
  'unsubscribe_text',
  'unsubscribe_url',
  'use_unsubscribe',
  'bot_holidays',
  'bot_warm_up',
  'latest_unresolved_bot_errors',
  'outreach_emails_sent_today',
];

export interface BotsApiIndexSchema {
  data: BotApiSchema[];
  meta: MetaSchema;
  included?: BotIncludedSchema;
}

export interface BotsIndexPayload {
  fields?: Record<string, string[]>;
  page?: number;
  perPage?: number;
  q?: string | null;
}

export interface BotBounceRatesApiSchema {
  bouncedCount: number;
  bouncedRate: number;
  date: string;
  reasons: Record<string, number>;
  sentCount: number;
}

export interface BotResolveErrorPayload {
  category: string;
  isResumingCampaigns: boolean;
}
export type BotResolveMutationResult = UseMutationResult<void, unknown, BotResolveErrorPayload>;

export interface CommonBotCredentials {
  emailAddress: string;
  name: string;
}
export interface SendGridBotPayload extends CommonBotCredentials {
  token?: string;
}
export type SendGridBotCreateMutationResult = UseMutationResult<
  string | number,
  unknown,
  SendGridBotPayload
>;
export type SendGridBotReconnectMutationResult = UseMutationResult<
  BotApiSchema,
  unknown,
  SendGridBotPayload
>;
